import { ScrollType, buildColumn, getDefaultPaginationSettings, getDefaultTablePaginationConfig, handleResponse, useHandleOnChangeTableLogic } from '@/Helper';
import { deviceAdminApi } from '@/api';
import { CustomFilters, Item, SearchColumn } from '@/components/common';
import { NavigationList } from '@/components/common/NavigationList/NavigationList';
import { GeneralSubPermissionColumn, NavigationListItem, SortDirections, TupleTyped } from '@/model/CommonModel';
import { GeneralPermission, GeneralSubPermission, ObjectTypes, RoleGeneralPermissionContext, RoleTab } from '@/model/DeviceAdminModel';
import { Logger } from '@/model/LoggingModel';
import { GeneralPermissionPaginationSetting, GrantedGeneralPermission } from '@/model/RolesModel';
import { Spin, Table } from 'antd';
import {
	ColumnsType,
	FilterValue,
	RowSelectMethod,
	SortOrder,
	SorterResult,
	TableCurrentDataSource,
	TablePaginationConfig,
	TableRowSelection,
} from 'antd/lib/table/interface';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { batch } from 'react-redux';
import { CloneFromRole } from '../CloneFromRole/CloneFromRole';
import { setGeneralPermissionsAction } from '../RoleContext/action';
import { RoleStore } from '../RoleContext/context';
import styles from './generalPermissions.module.scss';

type Props = {};

const adminRoleId: number = 1;
const defaultPagination: GeneralPermissionPaginationSetting = { ...getDefaultPaginationSettings(), SelectedObjectTypes: [] };
const getTableSortDirection = (sortDirection: SortDirections): SortOrder => {
	let sortOrder: SortOrder;
	if (sortDirection === SortDirections.Ascend) {
		sortOrder = 'ascend';
	} else if (sortDirection === SortDirections.Descend) {
		sortOrder = 'descend';
	} else {
		sortOrder = null;
	}

	return sortOrder;
};
const getRowClassName = (record: GeneralSubPermissionColumn) => {
	let rowClassName: string = '';
	if (record.ObjectType === ObjectTypes.doorGroups) {
		rowClassName = styles.doorGroupRow;
	} else if (record.ObjectType === ObjectTypes.masterDoorGroups) {
		rowClassName = styles.masterDoorGroupRow;
	} else if (record.ObjectType === ObjectTypes.timeZones) {
		if (Number(record.ObjectId) >= 3) {
			rowClassName = record.InUseByController ? styles.inUse : styles.notReferenced;
		} else {
			rowClassName = styles.systemTimeZone;
		}
	} else if (record.ObjectType === ObjectTypes.masterTimeZones || record.ObjectType === ObjectTypes.grandMasterTimeZones) {
		rowClassName = record.InUseByController ? styles.inUse : styles.notReferenced;
	}

	return rowClassName;
};

const GeneralPermissions: React.FC<Props> = () => {
	//#region state management variables
	const {
		roleContextInitialState: {
			props: { roleId },
			generalPermissions: { grantedSubPermissions, modifiedSubPermissions },
		},
		dispatcher,
	} = useContext(RoleStore);

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [isTableLoading, setIsTableLoading] = useState<boolean>(false);
	const [selectedGeneralPermissionId, setSelectedGeneralPermissionId] = useState<number>(0);
	const [resetSearchColumn, setResetSearchColumn] = useState<boolean>(false);
	const [generalPermissions, setGeneralPermissions] = useState<GeneralPermission[]>([]);
	const [paginationSettings, setPaginationSettings] = useState<GeneralPermissionPaginationSetting>(defaultPagination);
	const [subPermissions, setSubPermissions] = useState<GeneralSubPermissionColumn[]>([]);
	const isSearchPerformed = useRef<boolean>(resetSearchColumn);
	const handleOnChangeTableLogic = useHandleOnChangeTableLogic();
	//#endregion

	useEffect(() => {
		deviceAdminApi
			.getGeneralPermissions(roleId)
			.then(res => {
				if (!handleResponse(res)) {
					batch(() => {
						const {
							Entity: { GeneralPermissions, GrantedSubPermissions },
						} = res;
						setGeneralPermissions(GeneralPermissions);
						const newModifiedSubPermissions: Record<number, Partial<GeneralSubPermission>[]> = {};
						GeneralPermissions.forEach(gp => {
							gp.ObjectTypes.forEach(objectType => {
								newModifiedSubPermissions[objectType] = [];
							});
						});
						dispatcher(
							setGeneralPermissionsAction({
								grantedSubPermissions: GrantedSubPermissions,
								modifiedSubPermissions: newModifiedSubPermissions,
							})
						);
						if (GeneralPermissions.length) {
							const generalPermission: GeneralPermission = GeneralPermissions[0];
							setSelectedGeneralPermissionId(generalPermission.Id);
							const fetchOptions = getFetchOptions(generalPermission.Id, GeneralPermissions, GrantedSubPermissions);
							fetchData(fetchOptions.Item1, fetchOptions.Item2);
						}
					});
				}
			})
			.finally(() => {
				setIsLoading(false);
			});
	}, []);

	const fetchData = async (pagination: GeneralPermissionPaginationSetting, grantedGeneralPermissions: GrantedGeneralPermission[]) => {
		setIsTableLoading(true);
		try {
			const response = await deviceAdminApi.getGeneralSubPermissions(pagination, grantedGeneralPermissions);
			if (!handleResponse(response)) {
				const {
					Entity: { GeneralPermissions, TotalSelectedItems },
				} = response;
				const clonedPaginationState: GeneralPermissionPaginationSetting = {
					...pagination,
					TotalItems: TotalSelectedItems,
				};
				batch(() => {
					setPaginationSettings(clonedPaginationState);
					setSubPermissions(
						GeneralPermissions.map<GeneralSubPermissionColumn>(x => ({ ...x, Action: undefined, key: `${x.ObjectType}-${x.ObjectId}` }))
					);
				});
			}
		} catch (err) {
			Logger.writeErrorLog(`${err.name}: ${err.message}`);
		}
		batch(() => {
			setResetSearchColumn(false);
			setIsTableLoading(false);
		});
	};

	const clearSelection = (selectedGeneralPermission: GeneralPermission) => {
		let hasChanged: boolean = false;
		const clonedGrantedSubPermissions: Record<number, number[]> = { ...grantedSubPermissions };
		const clonedModifiedSubPermissions: Record<number, Partial<GeneralSubPermission>[]> = { ...modifiedSubPermissions };
		selectedGeneralPermission?.ObjectTypes?.forEach(objectType => {
			if (clonedGrantedSubPermissions) {
				clonedGrantedSubPermissions[objectType].forEach(gsp => {
					const tempSubPermission = clonedModifiedSubPermissions[objectType].find(sp => sp.ObjectId === gsp);
					if (!tempSubPermission) {
						clonedModifiedSubPermissions[objectType].push({
							ObjectId: gsp,
							ObjectType: objectType,
						});
					}
				});
				if (objectType === ObjectTypes.timeZones) {
					hasChanged = clonedGrantedSubPermissions[objectType].length > 2;
					clonedGrantedSubPermissions[objectType] = clonedGrantedSubPermissions[objectType].filter(objectId => objectId <= 2);
				} else {
					hasChanged = clonedGrantedSubPermissions[objectType].length > 0;
					clonedGrantedSubPermissions[objectType] = [];
				}
				clonedModifiedSubPermissions[objectType].forEach(cmsp => (cmsp.IsChecked = objectType === ObjectTypes.timeZones && cmsp.ObjectId <= 2));
			}
		});

		const generalPermissions: Partial<RoleGeneralPermissionContext> = {
			grantedSubPermissions: clonedGrantedSubPermissions,
			modifiedSubPermissions: clonedModifiedSubPermissions,
		};
		if (hasChanged) {
			generalPermissions.isDirty = true;
		}
		dispatcher(setGeneralPermissionsAction(generalPermissions));
	};

	const handleSelectAll = () => {
		setIsTableLoading(true);
		const clonedGrantedSubPermissions: Record<number, number[]> = { ...grantedSubPermissions };
		const clonedModifiedSubPermissions: Record<number, Partial<GeneralSubPermission>[]> = { ...modifiedSubPermissions };
		deviceAdminApi
			.getGeneralPermissionsByObjectTypes(adminRoleId, selectedGeneralPermission?.ObjectTypes)
			.then(response => {
				if (!handleResponse(response)) {
					let hasChanged: boolean = false;
					selectedGeneralPermission?.ObjectTypes?.forEach(objectType => {
						if (!hasChanged) {
							hasChanged = clonedGrantedSubPermissions[objectType].length !== response.Entity[objectType].length;
						}
						clonedGrantedSubPermissions[objectType] = response.Entity[objectType];
						clonedModifiedSubPermissions[objectType] = response.Entity[objectType].map<Partial<GeneralSubPermission>>(item => ({
							ObjectId: item,
							ObjectType: objectType,
							IsChecked: true,
						}));
					});

					const generalPermissions: Partial<RoleGeneralPermissionContext> = {
						grantedSubPermissions: clonedGrantedSubPermissions,
						modifiedSubPermissions: clonedModifiedSubPermissions,
					};
					if (hasChanged) {
						generalPermissions.isDirty = true;
					}
					dispatcher(setGeneralPermissionsAction(generalPermissions));
				}
			})
			.catch(err => Logger.writeErrorLog(`${err.name}: ${err.message}`))
			.finally(() => {
				setIsTableLoading(false);
			});
	};

	const setSearchPerformed = (value: boolean) => {
		isSearchPerformed.current = value;
	};

	const handleOnSearch = (searchedValue: string) => {
		const fetchOptions = getFetchOptions(selectedGeneralPermissionId, generalPermissions, grantedSubPermissions);
		fetchData(
			{ ...defaultPagination, SearchedValue: searchedValue, SelectedObjectTypes: fetchOptions.Item1.SelectedObjectTypes, PageSize: pagination.pageSize },
			fetchOptions.Item2
		);
	};

	const handleChangePagination = (page: number, pageSize: number) => {
		if (pageSize !== paginationSettings.PageSize) {
			setPaginationSettings({ ...paginationSettings, PageNumber: page, PageSize: pageSize });
		} else {
			setPaginationSettings({ ...paginationSettings, PageNumber: page });
		}
	};

	const handleOnChangeTable = (
		pagination: TablePaginationConfig,
		filters: Record<string, FilterValue>,
		sorter: SorterResult<GeneralSubPermissionColumn>,
		extra: TableCurrentDataSource<GeneralSubPermissionColumn>
	) => {
		if (
			extra.action === 'sort' &&
			sorter.field === 'ObjectType' &&
			extra.currentDataSource?.length > 0 &&
			extra.currentDataSource[0].ObjectType === ObjectTypes.graphics
		) {
			sorter.field = 'Type';
		}
		const { current, pageSize, shouldUpdateSearchResults, shouldUpdateTableResults, sortField, sortOrder } = handleOnChangeTableLogic({
			clearSelection: () => {},
			filters,
			handleChangePagination,
			isSearchPerformed,
			pagination,
			shouldResetSearchColumn: false,
			sorter,
			tablePaginationSetting: paginationSettings,
		});
		if (shouldUpdateTableResults || shouldUpdateSearchResults) {
			const fetchOptions = getFetchOptions(selectedGeneralPermissionId, generalPermissions, grantedSubPermissions);
			fetchData(
				{
					...paginationSettings,
					PageNumber: current,
					PageSize: pageSize,
					SortDirection: sortOrder,
					SortField: sortField.toString(),
				},
				fetchOptions.Item2
			);
		}
	};

	const handlePermissionsSelected = (
		selectedGeneralPermission: GeneralPermission,
		newSelectedRowKeys: React.Key[],
		rowSelectedMethod: RowSelectMethod,
		subPermissions: GeneralSubPermissionColumn[]
	) => {
		if (rowSelectedMethod === 'all') {
			const checkAll: boolean = newSelectedRowKeys?.length > selectedRowKeys.length;
			const clonedGrantedSubPermissions: Record<number, number[]> = { ...grantedSubPermissions };
			const clonedModifiedSubPermissions: Record<number, Partial<GeneralSubPermission>[]> = { ...modifiedSubPermissions };
			selectedGeneralPermission?.ObjectTypes?.forEach(objectType => {
				if (clonedGrantedSubPermissions) {
					const selectedRowKeysByType: number[] = [];
					newSelectedRowKeys.forEach((selectedKey: React.Key) => {
						const [keyObjectType, keyObjectId] = selectedKey.toString().split('-');
						if (keyObjectType === objectType.toString()) {
							selectedRowKeysByType.push(Number(keyObjectId));
						}
					});
					clonedGrantedSubPermissions[objectType] = selectedRowKeysByType;
				}
			});

			subPermissions.forEach(sp => {
				const tempSubPermission: Partial<GeneralSubPermission> = clonedModifiedSubPermissions[sp.ObjectType].find(
					csp => csp.ObjectId === sp.ObjectId && csp.ObjectType === sp.ObjectType
				);
				if (tempSubPermission) {
					tempSubPermission.IsChecked = checkAll;
				} else {
					clonedModifiedSubPermissions[sp.ObjectType].push({
						...sp,
						IsChecked: checkAll,
					});
				}
			});

			dispatcher(
				setGeneralPermissionsAction({
					isDirty: true,
					grantedSubPermissions: clonedGrantedSubPermissions,
					modifiedSubPermissions: clonedModifiedSubPermissions,
				})
			);
		}
	};

	const handleOnSelectItemOnTable = (clickedGeneralSubPermission: GeneralSubPermission, isChecked: boolean) => {
		const { ObjectId, ObjectType } = clickedGeneralSubPermission;
		const clonedGrantedSubPermissions = { ...grantedSubPermissions };
		const clonedModifiedSubPermissions = { ...modifiedSubPermissions };
		if (clonedGrantedSubPermissions) {
			const storedGrantedSubPermissionIndex: number = clonedGrantedSubPermissions[ObjectType].findIndex(p => p === ObjectId);
			if (isChecked && storedGrantedSubPermissionIndex === -1) {
				clonedGrantedSubPermissions[ObjectType].push(ObjectId);
			} else {
				clonedGrantedSubPermissions[ObjectType].splice(storedGrantedSubPermissionIndex, 1);
			}
		}
		if (clonedModifiedSubPermissions) {
			const storedModifiedSubPermission = clonedModifiedSubPermissions[ObjectType]?.find(sp => sp.ObjectId === ObjectId);
			if (storedModifiedSubPermission) {
				storedModifiedSubPermission.IsChecked = isChecked;
			} else {
				clonedModifiedSubPermissions[ObjectType].push({
					ObjectId,
					ObjectType,
					IsChecked: isChecked,
				});
			}
		}
		dispatcher(
			setGeneralPermissionsAction({
				isDirty: true,
				grantedSubPermissions: clonedGrantedSubPermissions,
				modifiedSubPermissions: clonedModifiedSubPermissions,
			})
		);
	};

	const getFetchOptions = (
		selectedGeneralPermissionId: number,
		generalPermissions: GeneralPermission[],
		grantedSubPermissions: Record<number, number[]>
	): TupleTyped<GeneralPermissionPaginationSetting, GrantedGeneralPermission[]> => {
		let retValue: TupleTyped<GeneralPermissionPaginationSetting, GrantedGeneralPermission[]> = { Item1: { ...paginationSettings }, Item2: [] };
		const selectedGeneralPermission = generalPermissions.find(x => x.Id === selectedGeneralPermissionId);
		if (selectedGeneralPermission) {
			retValue.Item1.SelectedObjectTypes = selectedGeneralPermission.ObjectTypes;
			selectedGeneralPermission.ObjectTypes.forEach(x => {
				grantedSubPermissions[x].forEach(y => retValue.Item2.push({ ObjectType: x, ObjectId: y }));
			});
		}

		return retValue;
	};

	const handleOnClickGeneralPermission = (item: NavigationListItem) => {
		const id = item.Id as number;
		batch(() => {
			setSelectedGeneralPermissionId(id);
			setResetSearchColumn(true);
		});
		const fetchOptions = getFetchOptions(id, generalPermissions, grantedSubPermissions);
		fetchData({ ...defaultPagination, SelectedObjectTypes: fetchOptions.Item1.SelectedObjectTypes }, fetchOptions.Item2);
	};

	const handleOnApplyFilter = (filters: any[]) => {
		const fetchOptions = getFetchOptions(selectedGeneralPermissionId, generalPermissions, grantedSubPermissions);
		const selectedObjectTypes = filters.length !== 0 ? filters : fetchOptions.Item1.SelectedObjectTypes;
		fetchData({ ...defaultPagination, SelectedObjectTypes: selectedObjectTypes }, fetchOptions.Item2);
	};

	const selectedRowKeys: React.Key[] = [];
	const selectedGeneralPermission = generalPermissions?.find(x => x.Id === selectedGeneralPermissionId);
	if (selectedGeneralPermission) {
		selectedGeneralPermission.ObjectTypes.forEach(x => {
			grantedSubPermissions[x].forEach(y => selectedRowKeys.push(`${x}-${y}`));
		});
	}

	const pagination: TablePaginationConfig = getDefaultTablePaginationConfig(
		false,
		page => {},
		paginationSettings.PageNumber,
		paginationSettings.PageSize,
		paginationSettings.TotalItems,
		undefined,
		selectedRowKeys
	);

	const rowSelection: TableRowSelection<GeneralSubPermissionColumn> = {
		preserveSelectedRowKeys: true,
		type: 'checkbox',
		selections:
			roleId === adminRoleId || subPermissions?.length === 0
				? undefined
				: [
						{
							key: 'SELECTION_ALL_TARGET',
							text: _('SelectAllData'),
							onSelect: () => handleSelectAll(),
						},
						{
							key: 'SELECTION_NONE',
							text: _('ClearAllData'),
							onSelect: () => clearSelection(selectedGeneralPermission),
						},
				  ],
		onChange: (selectedRowKeys: React.Key[], _, { type }) => {
			handlePermissionsSelected(selectedGeneralPermission, selectedRowKeys, type, [...subPermissions]);
		},
		getCheckboxProps: record => ({
			id: `rolesGeneralPermissionTableCheckbox-${record.key}`,
			disabled: roleId === adminRoleId || (record.ObjectType === ObjectTypes.timeZones && record.ObjectId < 3),
		}),
		onSelect: (record, selected) => {
			handleOnSelectItemOnTable(record, selected);
		},
		selectedRowKeys,
	};

	const columns: ColumnsType<GeneralSubPermissionColumn> = [
		{
			...buildColumn(
				selectedGeneralPermission ? `${_('Permissions')} for ${_(selectedGeneralPermission.Name)}` : _('Permissions'),
				'Name',
				'auto',
				'start'
			),
			key: 'generalPermissionNameColumn',
			sorter: selectedGeneralPermissionId !== 0,
			sortOrder: paginationSettings.SortField === 'Name' ? getTableSortDirection(paginationSettings.SortDirection) : null,
			...SearchColumn({
				maxLength: 20,
				dataIndex: 'Name',
				reset: resetSearchColumn,
				resetSearch: undefined,
				handleResetSearch: () => setResetSearchColumn(false),
				setSearchResults: (searchedValue: string) => handleOnSearch(searchedValue),
				setSearchPerformed: (value: boolean) => setSearchPerformed(value),
				disableInternalSearchMode: true,
				notVisible: selectedGeneralPermissionId === 0,
				skipSearch: true,
			}),
		},
	];

	let label: string = '';
	let legend: React.ReactNode = <></>;
	let scroll: ScrollType = { y: 400 };
	let hasDoorGroupType: boolean = false;
	let hasTimeZoneType: boolean = false;
	let hasLegacyGraphicsType: boolean = false;
	const options: Item[] = [];
	if (selectedGeneralPermission) {
		hasLegacyGraphicsType = selectedGeneralPermission.ObjectTypes.some(x => x === ObjectTypes.graphics);
		hasDoorGroupType = selectedGeneralPermission.ObjectTypes.some(x => x === ObjectTypes.doorGroups || x === ObjectTypes.masterDoorGroups);
		hasTimeZoneType = selectedGeneralPermission.ObjectTypes.some(
			x => x === ObjectTypes.timeZones || x === ObjectTypes.masterTimeZones || x === ObjectTypes.grandMasterTimeZones
		);

		if (hasDoorGroupType) {
			scroll.y = 330;
			label = _('FilterByDoorGroups');
			legend = (
				<div className={styles.referencesDoorGroups}>
					<div>
						<div />
						<label>{_('DoorGroup')}</label>
					</div>
					<div>
						<div />
						<label>{_('MasterDoorGroup')}</label>
					</div>
				</div>
			);
			options.push({
				key: ObjectTypes.doorGroups.toString(),
				label: _('DoorGroup'),
				value: ObjectTypes.doorGroups.toString(),
			});
			options.push({
				key: ObjectTypes.masterDoorGroups.toString(),
				label: _('MasterDoorGroup'),
				value: ObjectTypes.masterDoorGroups.toString(),
			});
		}

		if (hasTimeZoneType) {
			scroll.y = 330;
			label = _('FilterByTimeZones');
			legend = (
				<div className={styles.referencesTimeZones}>
					<div>
						<div />
						<label>{_('InUse')}</label>
					</div>
					<div>
						<div />
						<label>{_('NotReferenced')}</label>
					</div>
				</div>
			);
			options.push({
				key: ObjectTypes.timeZones.toString(),
				label: _('StandardTimeZones'),
				value: ObjectTypes.timeZones.toString(),
			});
			options.push({
				key: ObjectTypes.masterTimeZones.toString(),
				label: _('MasterTimeZones'),
				value: ObjectTypes.masterTimeZones.toString(),
			});
			options.push({
				key: ObjectTypes.grandMasterTimeZones.toString(),
				label: _('GrandMasterTimeZones'),
				value: ObjectTypes.grandMasterTimeZones.toString(),
			});
		}

		if (hasDoorGroupType || hasTimeZoneType || hasLegacyGraphicsType) {
			columns.push({
				...buildColumn(_('Type'), 'ObjectType', '150px', 'start'),
				key: 'generalPermissionTypeColumn',
				sorter: selectedGeneralPermissionId !== 0,
				render: (value, record: GeneralSubPermissionColumn) => <span>{_(record.ObjectTypeName)}</span>,
				sortOrder:
					paginationSettings.SortField === 'ObjectType' || paginationSettings.SortField === 'Type'
						? getTableSortDirection(paginationSettings.SortDirection)
						: null,
			});
		}
	}

	const setSelectedItemId = (id: number) => {
		if (id === 0) {
			setResetSearchColumn(true);

			batch(() => {
				setSelectedGeneralPermissionId(id);
				setSubPermissions([]);
				setPaginationSettings(defaultPagination);
				setResetSearchColumn(false);
			});
		} else {
			handleOnClickGeneralPermission({ Id: id, Name: '' });
		}
	};

	const getData = (): Record<number, number[]> => {
		return grantedSubPermissions;
	};

	const setData = (isDirty: boolean, generalPermissions: Record<number, number[]>) => {
		dispatcher(setGeneralPermissionsAction({ isDirty: isDirty, grantedSubPermissions: generalPermissions }));
	};

	return (
		<Spin wrapperClassName={styles.spin} size='large' tip={`${_('Loading')}...`} spinning={isLoading}>
			<CloneFromRole setLoading={setIsLoading} tab={RoleTab.GeneralPermission} getData={getData} setData={setData} />
			<div className={styles.container}>
				<div className={styles.leftContainer}>
					<NavigationList
						idPrefix='generalPermission'
						dataSource={generalPermissions}
						onElementClicked={handleOnClickGeneralPermission}
						selectedItemId={selectedGeneralPermissionId}
						setSelectedItemId={setSelectedItemId}
					/>
				</div>
				<div className={styles.rightContainer}>
					<Spin size='large' tip={`${_('Loading')}...`} spinning={isTableLoading}>
						{(hasDoorGroupType || hasTimeZoneType) && (
							<CustomFilters key={label} prefixId='generalPermission' label={label} options={options} onSelectionChange={handleOnApplyFilter} />
						)}
						<Table
							id='generalPermissionTable'
							className={styles.tableStyle}
							bordered
							pagination={pagination}
							scroll={scroll}
							size='small'
							dataSource={subPermissions}
							columns={columns}
							rowSelection={rowSelection}
							rowClassName={getRowClassName}
							onChange={handleOnChangeTable}
						/>
						{legend}
					</Spin>
				</div>
			</div>
		</Spin>
	);
};

export { GeneralPermissions };
